/**
 * i18n runtime — React-free, so `src/lib/format.ts` (imported by the
 * server too) can use it. Hooks live in `./react.ts`.
 *
 * Hungarian is bundled; English is a lazy chunk. Until it has loaded,
 * `getT()` answers in Hungarian and subscribers are notified when it lands.
 */

import { hu, type Messages } from './messages/hu';
import {
  dateTag,
  numberTag,
  resolveLocale,
  resolveUnitSystem,
  type Locale,
  type UnitSystem,
} from './locale';
import { AUTO_LOCALE_ENABLED } from './rollout';
import { deviceLanguageTags, localeSettings, subscribeToLocaleSettings } from './settings';

export type { Locale, UnitSystem } from './locale';
export type { Messages } from './messages/hu';

const loaded: Partial<Record<Locale, Messages>> = { hu };
const pending = new Map<Locale, Promise<void>>();
const listeners = new Set<() => void>();
/* Starts closed, before Firebase has resolved the signed-in user's claim. */
let localePreviewEnabled = AUTO_LOCALE_ENABLED;

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Enables the incomplete English/imperial preview only for the current
 * authorized session. Keeping this runtime state outside localStorage means
 * an explicit choice left by an admin cannot leak to the next account on the
 * same device.
 */
export function setLocalePreviewEnabled(enabled: boolean): void {
  const next = AUTO_LOCALE_ENABLED || enabled;
  if (localePreviewEnabled === next) return;
  localePreviewEnabled = next;
  void ensureLocaleLoaded();
  notify();
}

/** The language the user ASKED for (may not be loaded yet). */
export function preferredLocale(): Locale {
  const { language } = localeSettings();
  if (!localePreviewEnabled) return 'hu';
  return resolveLocale(language, deviceLanguageTags());
}

/** The language the UI is shown in right now. */
export function getLocale(): Locale {
  const locale = preferredLocale();
  return loaded[locale] ? locale : 'hu';
}

export function getT(): Messages {
  return loaded[getLocale()] ?? hu;
}

export function getUnitSystem(): UnitSystem {
  const { units } = localeSettings();
  if (!localePreviewEnabled) return 'metric';
  return resolveUnitSystem(units, deviceLanguageTags());
}

/** BCP 47 tag for `Intl.NumberFormat` — see `numberTag`. */
export function getNumberTag(): string {
  return numberTag(getLocale());
}

/** BCP 47 tag for dates and times — see `dateTag`. */
export function getDateTag(): string {
  return dateTag(getLocale(), deviceLanguageTags());
}

/** Loads the preferred language's dictionary if it is not bundled. */
export function ensureLocaleLoaded(): Promise<void> {
  const locale = preferredLocale();
  if (loaded[locale]) return Promise.resolve();
  let promise = pending.get(locale);
  if (!promise) {
    promise = import('./messages/en')
      .then((module) => {
        loaded.en = module.en;
        notify();
      })
      .catch(() => {
        // Chunk failed (offline right after a deploy): stay Hungarian, retry next time.
      })
      .finally(() => {
        pending.delete(locale);
      });
    pending.set(locale, promise);
  }
  return promise;
}

let watchingSettings = false;

/** Fires on a language/unit change and when a dictionary finishes loading. */
export function subscribeLocale(listener: () => void): () => void {
  if (!watchingSettings) {
    watchingSettings = true;
    subscribeToLocaleSettings(() => {
      void ensureLocaleLoaded();
      notify();
    });
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
